import { DurableObject } from "cloudflare:workers";
import type {
  Env,
  ChatMessage,
  Memory,
  SubAIVAState,
  TierConfig,
  WSMessage,
  WSResponse,
} from "./types";
import { TIER_CONFIGS } from "./types";

/**
 * SubAIVA Durable Object
 *
 * Each customer gets exactly ONE instance of this class.
 * It holds their conversation state, memory, preferences,
 * and communicates via WebSocket (hibernation mode for cost savings).
 *
 * Architecture:
 * - SQLite: conversation history, memories, preferences, state
 * - Vectorize: semantic memory search (via env binding)
 * - Workers AI: inference (via env binding)
 * - Alarms: memory consolidation, decay, health checks
 */
export class SubAIVA extends DurableObject<Env> {
  private state!: SubAIVAState;
  private tierConfig!: TierConfig;
  private initialized = false;

  constructor(ctx: DurableObjectState, env: Env) {
    super(ctx, env);

    // Block concurrent requests while initializing
    ctx.blockConcurrencyWhile(async () => {
      await this.initialize();
    });
  }

  /**
   * Initialize SQLite tables and load state from storage
   */
  private async initialize(): Promise<void> {
    if (this.initialized) return;

    const sql = this.ctx.storage.sql;

    // Create tables
    sql.exec(`
      CREATE TABLE IF NOT EXISTS state (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL
      );

      CREATE TABLE IF NOT EXISTS messages (
        id INTEGER PRIMARY KEY AUTOINCREMENT,
        role TEXT NOT NULL,
        content TEXT NOT NULL,
        timestamp INTEGER NOT NULL
      );

      CREATE TABLE IF NOT EXISTS memories (
        id TEXT PRIMARY KEY,
        content TEXT NOT NULL,
        category TEXT NOT NULL DEFAULT 'general',
        importance REAL NOT NULL DEFAULT 0.5,
        created_at INTEGER NOT NULL,
        last_accessed INTEGER NOT NULL,
        access_count INTEGER NOT NULL DEFAULT 0
      );

      CREATE TABLE IF NOT EXISTS preferences (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at INTEGER NOT NULL
      );
    `);

    // Load or create state
    const stateRow = sql
      .exec("SELECT value FROM state WHERE key = 'subaiva_state'")
      .toArray();

    if (stateRow.length > 0) {
      this.state = JSON.parse(stateRow[0].value as string);
    } else {
      this.state = {
        customerId: "",
        tier: 1,
        systemPrompt: "You are a helpful AI assistant with persistent memory.",
        createdAt: Date.now(),
        lastActive: Date.now(),
        totalMessages: 0,
      };
    }

    this.tierConfig = TIER_CONFIGS[this.state.tier] || TIER_CONFIGS[1];
    this.initialized = true;
  }

  /**
   * Save state to SQLite
   */
  private saveState(): void {
    this.ctx.storage.sql.exec(
      "INSERT OR REPLACE INTO state (key, value) VALUES ('subaiva_state', ?)",
      JSON.stringify(this.state)
    );
  }

  // ─── PROVISIONING ────────────────────────────────────────

  /**
   * Provision this SubAIVA for a customer.
   * Called once when subscription starts.
   */
  async provision(customerId: string, tier: number, systemPrompt?: string): Promise<SubAIVAState> {
    this.state.customerId = customerId;
    this.state.tier = tier;
    this.state.createdAt = Date.now();
    this.state.lastActive = Date.now();
    if (systemPrompt) {
      this.state.systemPrompt = systemPrompt;
    }
    this.tierConfig = TIER_CONFIGS[tier] || TIER_CONFIGS[1];
    this.saveState();

    // Schedule first memory consolidation alarm (1 hour from now)
    const currentAlarm = await this.ctx.storage.getAlarm();
    if (!currentAlarm) {
      await this.ctx.storage.setAlarm(Date.now() + 60 * 60 * 1000);
    }

    return this.state;
  }

  /**
   * Update subscription tier (upgrade/downgrade)
   */
  async updateTier(tier: number): Promise<TierConfig> {
    this.state.tier = tier;
    this.tierConfig = TIER_CONFIGS[tier] || TIER_CONFIGS[1];
    this.saveState();
    return this.tierConfig;
  }

  /**
   * Get current status
   */
  async getStatus(): Promise<Record<string, unknown>> {
    const messageCount = this.ctx.storage.sql
      .exec("SELECT COUNT(*) as count FROM messages")
      .one();
    const memoryCount = this.ctx.storage.sql
      .exec("SELECT COUNT(*) as count FROM memories")
      .one();

    return {
      state: this.state,
      tier: this.tierConfig,
      messageCount: messageCount.count,
      memoryCount: memoryCount.count,
    };
  }

  // ─── CHAT (HTTP RPC) ────────────────────────────────────

  /**
   * Process a chat message and return AI response.
   * This is the core conversational method.
   */
  async chat(userMessage: string): Promise<string> {
    this.state.lastActive = Date.now();
    this.state.totalMessages++;

    // Store user message
    this.ctx.storage.sql.exec(
      "INSERT INTO messages (role, content, timestamp) VALUES (?, ?, ?)",
      "user",
      userMessage,
      Date.now()
    );

    // Retrieve relevant memories for context
    const memoryContext = await this.searchMemories(userMessage, 5);

    // Build conversation history (limited by tier)
    const recentMessages = this.ctx.storage.sql
      .exec(
        "SELECT role, content FROM messages ORDER BY id DESC LIMIT ?",
        this.tierConfig.maxConversationHistory
      )
      .toArray()
      .reverse();

    // Build the prompt with memory context
    const messages: Array<{ role: string; content: string }> = [
      {
        role: "system",
        content: this.buildSystemPrompt(memoryContext),
      },
    ];

    for (const msg of recentMessages) {
      messages.push({
        role: msg.role as string,
        content: msg.content as string,
      });
    }

    // Call Workers AI (or return echo in local dev without AI binding)
    let assistantMessage: string;
    try {
      if (!this.env.AI) {
        throw new Error("AI binding not available");
      }
      const response = await (this.env.AI as any).run(this.tierConfig.model, {
        messages,
      });

      assistantMessage =
        typeof response === "string"
          ? response
          : response && typeof response === "object" && "response" in response
            ? String((response as any).response ?? "")
            : "";
    } catch {
      // Local dev fallback — echo with memory context
      const memSummary = memoryContext.length > 0
        ? ` [${memoryContext.length} memories loaded]`
        : "";
      assistantMessage = `[Local dev — AI binding unavailable] Received: "${userMessage}"${memSummary}. Deploy to Cloudflare for full AI responses.`;
    }

    // Store assistant response
    this.ctx.storage.sql.exec(
      "INSERT INTO messages (role, content, timestamp) VALUES (?, ?, ?)",
      "assistant",
      assistantMessage,
      Date.now()
    );

    // Auto-capture memories from the conversation
    await this.autoCapture(userMessage);

    this.saveState();
    return assistantMessage;
  }

  // ─── MEMORY SYSTEM ──────────────────────────────────────

  /**
   * Store a memory with embedding in Vectorize
   */
  async storeMemory(
    content: string,
    category: string = "general",
    importance: number = 0.5
  ): Promise<Memory> {
    // Check tier limits
    const count = this.ctx.storage.sql
      .exec("SELECT COUNT(*) as count FROM memories")
      .one();
    if ((count.count as number) >= this.tierConfig.maxMemories) {
      // Evict lowest importance memory
      this.ctx.storage.sql.exec(
        "DELETE FROM memories WHERE id = (SELECT id FROM memories ORDER BY importance ASC, last_accessed ASC LIMIT 1)"
      );
    }

    const id = `mem_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const now = Date.now();

    const memory: Memory = {
      id,
      content,
      category,
      importance,
      createdAt: now,
      lastAccessed: now,
      accessCount: 0,
    };

    // Store in SQLite
    this.ctx.storage.sql.exec(
      "INSERT INTO memories (id, content, category, importance, created_at, last_accessed, access_count) VALUES (?, ?, ?, ?, ?, ?, ?)",
      id,
      content,
      category,
      importance,
      now,
      now,
      0
    );

    // Generate embedding and store in Vectorize (graceful when AI/Vectorize unavailable)
    try {
      if (this.env.AI && this.env.MEMORY_VECTORS) {
        const embeddingResult = await (this.env.AI as any).run(
          this.env.EMBEDDING_MODEL,
          { text: [content] }
        );

        if (embeddingResult?.data && embeddingResult.data.length > 0) {
          await this.env.MEMORY_VECTORS.upsert([
            {
              id,
              values: embeddingResult.data[0],
              metadata: {
                customerId: this.state.customerId,
                category,
                importance,
                createdAt: now,
              },
            },
          ]);
        }
      }
    } catch (e) {
      // Vectorize/AI may not be available in dev — log but don't fail
      console.error("Vectorize upsert failed:", e);
    }

    return memory;
  }

  /**
   * Search memories using semantic similarity via Vectorize
   */
  async searchMemories(query: string, topK: number = 5): Promise<Memory[]> {
    // If AI or Vectorize bindings are missing, fall back to SQLite keyword search
    if (!this.env.AI || !this.env.MEMORY_VECTORS) {
      return this.fallbackSearch(query, topK);
    }

    try {
      // Generate query embedding
      const embeddingResult = await (this.env.AI as any).run(
        this.env.EMBEDDING_MODEL,
        { text: [query] }
      );

      if (!embeddingResult?.data || embeddingResult.data.length === 0) {
        return this.fallbackSearch(query, topK);
      }

      // Query Vectorize
      const matches = await this.env.MEMORY_VECTORS.query(
        embeddingResult.data[0],
        {
          topK,
          returnMetadata: "all",
          filter: { customerId: this.state.customerId },
        }
      );

      if (!matches.matches || matches.matches.length === 0) {
        return this.fallbackSearch(query, topK);
      }

      // Fetch full memory content from SQLite
      const memories: Memory[] = [];
      for (const match of matches.matches) {
        const row = this.ctx.storage.sql
          .exec("SELECT * FROM memories WHERE id = ?", match.id)
          .toArray();

        if (row.length > 0) {
          const r = row[0];
          // Update access tracking
          this.ctx.storage.sql.exec(
            "UPDATE memories SET last_accessed = ?, access_count = access_count + 1 WHERE id = ?",
            Date.now(),
            match.id
          );

          memories.push({
            id: r.id as string,
            content: r.content as string,
            category: r.category as string,
            importance: r.importance as number,
            createdAt: r.created_at as number,
            lastAccessed: Date.now(),
            accessCount: (r.access_count as number) + 1,
          });
        }
      }

      return memories;
    } catch (e) {
      console.error("Vectorize search failed:", e);
      return this.fallbackSearch(query, topK);
    }
  }

  /**
   * Fallback: keyword search in SQLite when Vectorize unavailable
   */
  private fallbackSearch(query: string, limit: number): Memory[] {
    const keywords = query.toLowerCase().split(/\s+/).slice(0, 5);
    const likeClause = keywords.map(() => "LOWER(content) LIKE ?").join(" OR ");
    const params = keywords.map((k) => `%${k}%`);

    const rows = this.ctx.storage.sql
      .exec(
        `SELECT * FROM memories WHERE ${likeClause} ORDER BY importance DESC, last_accessed DESC LIMIT ?`,
        ...params,
        limit
      )
      .toArray();

    return rows.map((r) => ({
      id: r.id as string,
      content: r.content as string,
      category: r.category as string,
      importance: r.importance as number,
      createdAt: r.created_at as number,
      lastAccessed: r.last_accessed as number,
      accessCount: r.access_count as number,
    }));
  }

  /**
   * Auto-capture: extract memorable facts from user messages
   */
  private async autoCapture(userMessage: string): Promise<void> {
    // Simple heuristic auto-capture patterns
    const patterns = [
      { regex: /my name is (\w+)/i, category: "identity", importance: 0.9 },
      { regex: /i (?:work|am) (?:at|for|a) (.+?)(?:\.|,|$)/i, category: "identity", importance: 0.8 },
      { regex: /i (?:live|am based) in (.+?)(?:\.|,|$)/i, category: "identity", importance: 0.7 },
      { regex: /i (?:like|love|enjoy|prefer) (.+?)(?:\.|,|$)/i, category: "preference", importance: 0.6 },
      { regex: /(?:remember|don't forget|note that) (.+?)(?:\.|$)/i, category: "explicit", importance: 0.9 },
    ];

    for (const pattern of patterns) {
      const match = userMessage.match(pattern.regex);
      if (match) {
        await this.storeMemory(
          match[0],
          pattern.category,
          pattern.importance
        );
      }
    }
  }

  // ─── WEBSOCKET HIBERNATION ──────────────────────────────

  /**
   * Handle incoming HTTP requests.
   * WebSocket upgrade requests get handled here.
   */
  async fetch(request: Request): Promise<Response> {
    const url = new URL(request.url);

    // WebSocket upgrade
    if (request.headers.get("Upgrade") === "websocket") {
      const pair = new WebSocketPair();
      const [client, server] = Object.values(pair);

      // Accept with hibernation — DO sleeps when idle, zero cost
      this.ctx.acceptWebSocket(server);

      // Tag the WebSocket with customer info for identification
      server.serializeAttachment({
        customerId: this.state.customerId,
        connectedAt: Date.now(),
      });

      return new Response(null, { status: 101, webSocket: client });
    }

    // HTTP RPC endpoints (for non-WebSocket clients)
    if (url.pathname.endsWith("/chat") && request.method === "POST") {
      const body = (await request.json()) as { message: string };
      const response = await this.chat(body.message);
      return Response.json({ response });
    }

    if (url.pathname.endsWith("/status")) {
      const status = await this.getStatus();
      return Response.json(status);
    }

    if (url.pathname.endsWith("/memory") && request.method === "POST") {
      const body = (await request.json()) as {
        content: string;
        category?: string;
        importance?: number;
      };
      const memory = await this.storeMemory(
        body.content,
        body.category,
        body.importance
      );
      return Response.json(memory);
    }

    if (url.pathname.endsWith("/memory/search") && request.method === "POST") {
      const body = (await request.json()) as { query: string; topK?: number };
      const memories = await this.searchMemories(body.query, body.topK);
      return Response.json({ memories });
    }

    return new Response("SubAIVA DO Active", { status: 200 });
  }

  // ─── WEBSOCKET HIBERNATION HANDLERS ─────────────────────
  // These methods are called by the runtime even after hibernation wake-up

  async webSocketMessage(
    ws: WebSocket,
    message: string | ArrayBuffer
  ): Promise<void> {
    try {
      const msg: WSMessage = JSON.parse(
        typeof message === "string" ? message : new TextDecoder().decode(message)
      );

      let response: WSResponse;

      switch (msg.type) {
        case "chat": {
          const chatResponse = await this.chat(
            msg.payload.message as string
          );
          response = {
            type: "chat_response",
            payload: { response: chatResponse },
            requestId: msg.requestId,
          };
          break;
        }

        case "memory_store": {
          const memory = await this.storeMemory(
            msg.payload.content as string,
            msg.payload.category as string | undefined,
            msg.payload.importance as number | undefined
          );
          response = {
            type: "memory_result",
            payload: { memory },
            requestId: msg.requestId,
          };
          break;
        }

        case "memory_search": {
          const memories = await this.searchMemories(
            msg.payload.query as string,
            (msg.payload.topK as number) || 5
          );
          response = {
            type: "memory_result",
            payload: { memories },
            requestId: msg.requestId,
          };
          break;
        }

        case "status": {
          const status = await this.getStatus();
          response = {
            type: "status",
            payload: status,
            requestId: msg.requestId,
          };
          break;
        }

        default:
          response = {
            type: "error",
            payload: { error: `Unknown message type: ${msg.type}` },
            requestId: msg.requestId,
          };
      }

      ws.send(JSON.stringify(response));
    } catch (error) {
      ws.send(
        JSON.stringify({
          type: "error",
          payload: {
            error: error instanceof Error ? error.message : "Unknown error",
          },
        })
      );
    }
  }

  async webSocketClose(
    ws: WebSocket,
    code: number,
    reason: string,
    wasClean: boolean
  ): Promise<void> {
    ws.close(code, reason);
  }

  async webSocketError(ws: WebSocket, error: unknown): Promise<void> {
    console.error("WebSocket error:", error);
    ws.close(1011, "Internal error");
  }

  // ─── ALARM: MEMORY CONSOLIDATION ────────────────────────

  /**
   * Alarm handler — runs periodically for memory maintenance.
   * - Decay old memories (tier-based)
   * - Consolidate similar memories
   * - Prune over-limit memories
   */
  async alarm(): Promise<void> {
    console.log(
      `[SubAIVA ${this.state.customerId}] Memory consolidation alarm fired`
    );

    // 1. Decay old memories based on tier config
    if (this.tierConfig.memoryDecayDays > 0) {
      const cutoff =
        Date.now() - this.tierConfig.memoryDecayDays * 24 * 60 * 60 * 1000;

      // Delete old, low-importance memories
      const deleted = this.ctx.storage.sql.exec(
        "DELETE FROM memories WHERE last_accessed < ? AND importance < 0.7",
        cutoff
      );
      console.log(`Decayed memories older than ${this.tierConfig.memoryDecayDays} days`);
    }

    // 2. Prune if over tier limit
    const count = this.ctx.storage.sql
      .exec("SELECT COUNT(*) as count FROM memories")
      .one();

    if ((count.count as number) > this.tierConfig.maxMemories) {
      const excess = (count.count as number) - this.tierConfig.maxMemories;
      this.ctx.storage.sql.exec(
        "DELETE FROM memories WHERE id IN (SELECT id FROM memories ORDER BY importance ASC, last_accessed ASC LIMIT ?)",
        excess
      );
      console.log(`Pruned ${excess} excess memories`);
    }

    // 3. Trim conversation history beyond tier limit
    const msgCount = this.ctx.storage.sql
      .exec("SELECT COUNT(*) as count FROM messages")
      .one();

    const maxHistory = this.tierConfig.maxConversationHistory * 2; // keep 2x for context
    if ((msgCount.count as number) > maxHistory) {
      this.ctx.storage.sql.exec(
        "DELETE FROM messages WHERE id NOT IN (SELECT id FROM messages ORDER BY id DESC LIMIT ?)",
        maxHistory
      );
      console.log(`Trimmed conversation history to ${maxHistory} messages`);
    }

    // Re-schedule alarm (every 6 hours)
    await this.ctx.storage.setAlarm(Date.now() + 6 * 60 * 60 * 1000);
  }

  // ─── HELPERS ────────────────────────────────────────────

  /**
   * Build system prompt with memory context injected
   */
  private buildSystemPrompt(memories: Memory[]): string {
    let prompt = this.state.systemPrompt;

    if (memories.length > 0) {
      prompt += "\n\n## Your memories about this user:\n";
      for (const mem of memories) {
        prompt += `- [${mem.category}] ${mem.content}\n`;
      }
      prompt +=
        "\nUse these memories naturally in conversation. Do not mention that you are reading from a memory database.";
    }

    return prompt;
  }
}
